import {
  Icon,
  NewTask,
  DeleteModal,
  DisabledTag,
  getTranslatedText,
  B1,
  Tag,
  BackgroundIcon,
  Row,
  AccessControl,
  isAuthorised
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import StatusHolder from '@nile/projects/src/Components/Elements/StatusHolder';
import { renderTime } from '@nile/projects/src/Utils';
import { Card, Avatar } from '@superops/ui-design-system';
import { Trash, Pen, Folder, Stopwatch } from '@styled-icons/fa-solid';
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import dompurify from 'dompurify';
import { PROJECT_STATUSES } from '@nile/projects/src/Components/Cards/ProjectListItem';
import { SVGIcon } from '@superops/super-icons';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';

const OuterWrapper = styled.div`
  display: flex;
  flex: 3 1;
  align-items: center;
  .Polaris-Heading {
    font-weight: 700;
    font-size: 1.4rem !important;
    color: #011fff !important;
  }
  &.secondCol {
    justify-content: flex-end;
    flex: 1 1;
    &.no-visibility {
      visibility: hidden;
    }
  }

  .deleteBtn :hover {
    color: #e02020;
  }
`;

const TaskPreview = styled.div`
  display: grid;
  gap: 2%;
  grid-template-columns: ${(props) =>
    props.hideDelete ? '20% repeat(4, 18%)' : '20% repeat(4, 17%) 2%'};
  .status-tag {
    max-width: fit-content;
  }
`;

const Wrapper = styled.div`
  .Polaris-Card {
    border-radius: 0px !important;
    margin: 0 !important;
    box-shadow: none !important;
    margin-bottom: 20px;
    padding: 5px 20px;
  }
  .dragHandle {
    visibility: hidden;
  }
  &:hover .dragHandle {
    visibility: visible;
  }
`;

const TaskCard = ({
  intl,
  task,
  handleTaskDeletion,
  handleTaskUpdation,
  index,
  module,
  hideSectionsInTaskForm = [],
  details,
  disableActions,
  disableDelete,
  disableEdit,
  isTemplate,
  enableDrag
}) => {
  const [sheetActive, setSheetActive] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };
  const { title, techGroup, technician, status, estimatedTime } = task;

  const handleDeletion = (toggleSheet) => {
    handleTaskDeletion(index);
    if (toggleSheet) {
      toggleSheetActive();
    }
  };

  const handleUpdation = (updated) => {
    handleTaskUpdation(index, updated);
    toggleSheetActive();
  };

  return (
    <Wrapper
      className="taskCard mb-20 mt-20"
      data-qa={`task-${index}`}
      onMouseOver={() => setShowOptions(true)}
      onMouseLeave={() => setShowOptions(false)}
    >
      <Row gap={5}>
        {enableDrag ? (
          <SuperSortable.DragHandle className="dragHandle" />
        ) : (
          <div className="pl-15" />
        )}
        <TaskPreview
          hideDelete={disableActions || disableDelete}
          className="full-width"
        >
          <div className="ellipsis">
            {disableEdit ? (
              <div>{title}</div>
            ) : (
              <B1>
                <NewTask
                  addOrUpdateTechGroup={(type, updated) =>
                    handleUpdation(updated)
                  }
                  state={sheetActive}
                  toggleSheetActive={toggleSheetActive}
                  name={title}
                  hideIcon
                  isLink
                  data={task}
                  module={module || 'PROJECT'}
                  details={details || { operation: 'CREATE' }}
                  onDeleteHandler={() => handleDeletion(true)}
                  hideSections={hideSectionsInTaskForm}
                  isTemplate={isTemplate}
                />
              </B1>
            )}
          </div>
          <div className="uppercase">
            <div className="status-tag">
              <Tag
                active={PROJECT_STATUSES.ACTIVE.includes(
                  status ||
                    getTranslatedText(intl, 'project.task.status.planned')
                )}
                smooth={PROJECT_STATUSES.NORMAL.includes(
                  status ||
                    getTranslatedText(intl, 'project.task.status.planned')
                )}
                size="small"
              >
                {isTemplate
                  ? 'TEMPLATE'
                  : status ||
                    getTranslatedText(intl, 'project.task.status.planned')}
              </Tag>
            </div>
          </div>
          <div className="fx actr ellipsis">
            {!hideSectionsInTaskForm.includes('techGroup') && (
              <>
                <div className="mr-10 fx center">
                  <SVGIcon name="TechnicianGroup" />
                </div>
                {techGroup ? (
                  <div>{techGroup?.name || 'NA'}</div>
                ) : (
                  <DisabledTag>
                    <FormattedMessage id="app.noData.message" />
                  </DisabledTag>
                )}
              </>
            )}
          </div>
          <div className="fx actr ellipsis">
            {!hideSectionsInTaskForm.includes('technician') && (
              <>
                <div className="mr8">
                  <Avatar customer name="technician" size="extraSmall" />
                </div>
                {technician ? (
                  `${technician.firstName} ${technician.lastName}`
                ) : (
                  <DisabledTag>
                    <FormattedMessage id="app.noData.message" />
                  </DisabledTag>
                )}
              </>
            )}
          </div>
          <div className="fx actr ellipsis">
            <div className="mr-10 fx center">
              <SVGIcon name="EstimatedTime" />
            </div>
            {renderTime(estimatedTime) || (
              <DisabledTag>
                <FormattedMessage id="app.noData.message" />
              </DisabledTag>
            )}
          </div>
          {!disableActions && (
            <OuterWrapper
              className={showOptions ? 'secondCol' : 'secondCol no-visibility'}
            >
              {!disableDelete && (
                <div className="deleteBtn" data-qa="deleteTaskBtn">
                  <DeleteModal
                    description={getTranslatedText(
                      intl,
                      'ticket.details.task.delete.confirm'
                    )}
                    handleDeleteConfirmation={handleDeletion}
                    title={getTranslatedText(
                      intl,
                      'ticket.details.task.delete.text'
                    )}
                    destructive
                    plain
                  />
                </div>
              )}
            </OuterWrapper>
          )}
        </TaskPreview>
      </Row>
    </Wrapper>
  );
};

export default injectIntl(TaskCard);
